import { useEffect, useState, useRef } from 'react';
import { createSocketManager } from '@/core/services/upload/utils/socketManager';
import { log } from '@/core/debug/anomalyLogger';

export interface SocketProgress {
  progress: number;
  message?: string;
  etaFormatted?: string;
  step?: string;
  messageKey?: string;
  messageParams?: Record<string, unknown>;
  currentStep?: number;
  totalSteps?: number;
  status?: 'processing' | 'completed' | 'error';
  lastProgressAt: number | null;
}

const initial: SocketProgress = { progress: 0, lastProgressAt: null };

type AnomalyProgressPayload = Pick<
  SocketProgress,
  'progress' | 'step' | 'messageKey' | 'messageParams' | 'currentStep' | 'totalSteps' | 'status'
> & {
  uploadId?: string;
  // Backend sends `null` to signal "ETA not yet computable" (FE shows
  // localized 'Calculating...'). Override the SocketProgress narrow type
  // to allow null in the wire payload.
  etaFormatted?: string | null;
};

/**
 * Subscribes to the SocketIO `uploadId` room and exposes the latest backend
 * progress event for the data-processing (anomaly detection) pipeline.
 *
 * Events listened:
 *   - `anomaly_progress`     — emitted by the anomaly pipeline preprocess phase
 *                              (Bekend/domains/adjustments/api/adjustments.py ~L689)
 *                              Payload: { uploadId, step, progress }
 *   - `processing_progress`  — emitted by the generic ProgressTracker for
 *                              long-running ops (services/progress.py ~L284,339)
 *                              Payload: { uploadId, progress, messageKey, step, etaFormatted, ... }
 *   - `onProgress` callback  — defensive catch-all via SocketManager helper
 *
 * Events for OTHER uploadIds are ignored (server emits to a room, but reuse
 * of the same SocketManager singleton may surface cross-upload events in edge
 * cases).
 *
 * Lifecycle: joins on mount/uploadId-change, leaves on unmount/change.
 * Returns initial { progress: 0 } when uploadId is null.
 *
 * E2E escape hatch: if `window.__e2eMockSocketManager` is set, it is used as
 * the factory instead of `createSocketManager`. This allows Playwright tests to
 * inject a deterministic mock without touching module internals.
 */
export function useAnomalySocket(uploadId: string | null): SocketProgress {
  const [state, setState] = useState<SocketProgress>(initial);
  // Use a ref so the effect cleanup always has access to the same manager instance.
  const managerRef = useRef<ReturnType<typeof createSocketManager> | null>(null);

  useEffect(() => {
    log.hook('useAnomalySocket', 'effect run', { uploadId });
    if (!uploadId) {
      log.hook('useAnomalySocket', 'no uploadId, skipping join');
      setState(initial);
      return;
    }

    // E2E escape hatch: Playwright tests can inject a mock factory via window.
    // Gated on import.meta.env.DEV so Vite tree-shakes this branch in production builds.
    // CRITICAL: use a typeof check (not ?? or ||) — `false ?? x` returns false
    // (?? only fallbacks on null/undefined), and `false || x` is fine but typeof
    // is the most defensive: only call e2eMock when it's actually a function.
    const e2eMock =
      import.meta.env.DEV &&
      typeof window !== 'undefined' &&
      (window as unknown as { __e2eMockSocketManager?: () => unknown }).__e2eMockSocketManager;
    const factory =
      typeof e2eMock === 'function'
        ? (e2eMock as typeof createSocketManager)
        : createSocketManager;

    if (!managerRef.current) {
      managerRef.current = factory();
    }
    const mgr = managerRef.current;

    mgr.joinRoom(uploadId);
    log.sock('join_room', { uploadId });

    // Handler for anomaly_progress: { uploadId, step, progress, etaFormatted,
    // messageKey, messageParams, currentStep, totalSteps, status }
    const handleAnomaly = (data: AnomalyProgressPayload) => {
      if (data.uploadId !== uploadId) {
        log.sock('anomaly_progress IGNORED (foreign uploadId)', { received: data.uploadId, expected: uploadId });
        return;
      }
      log.sock('anomaly_progress', data);
      setState((prev) => ({
        ...prev,
        progress: typeof data.progress === 'number' ? data.progress : prev.progress,
        step: data.step ?? prev.step,
        // Backend sends `null` when ETA is not yet computable so the FE i18n
        // fallback (`t('loading.calculating')`) renders the localized string.
        // Treat null as "reset" — without this the previous phase's ETA would
        // stick when a new phase begins. Field absent → preserve previous.
        etaFormatted:
          data.etaFormatted === null
            ? undefined
            : data.etaFormatted ?? prev.etaFormatted,
        messageKey: data.messageKey ?? prev.messageKey,
        messageParams: data.messageParams ?? prev.messageParams,
        currentStep: typeof data.currentStep === 'number' ? data.currentStep : prev.currentStep,
        totalSteps: typeof data.totalSteps === 'number' ? data.totalSteps : prev.totalSteps,
        status: data.status ?? prev.status,
        lastProgressAt: Date.now(),
      }));
    };

    // Handler for processing_progress: { uploadId, progress, messageKey, step, etaFormatted, ... }
    // Also used as the generic onProgress defensive catch-all.
    const handleGeneric = (data: Record<string, unknown>) => {
      if (data.uploadId !== uploadId) {
        log.sock('processing_progress IGNORED (foreign uploadId)', { received: data.uploadId, expected: uploadId });
        return;
      }
      log.sock('processing_progress', data);
      setState((prev) => ({
        ...prev,
        progress:
          typeof data.progress === 'number' ? data.progress : prev.progress,
        message:
          (data.message as string | undefined) ??
          (data.messageKey as string | undefined) ??
          prev.message,
        etaFormatted:
          (data.etaFormatted as string | undefined) ?? prev.etaFormatted,
        step: (data.step as string | undefined) ?? prev.step,
        lastProgressAt: Date.now(),
      }));
    };

    mgr.on('anomaly_progress', handleAnomaly);
    mgr.on('processing_progress', handleGeneric);
    mgr.onProgress(handleGeneric);

    return () => {
      log.hook('useAnomalySocket', 'cleanup', { uploadId });
      log.sock('leave_room', { uploadId });
      mgr.off('anomaly_progress', handleAnomaly);
      mgr.off('processing_progress', handleGeneric);
      mgr.offProgress(handleGeneric);
      mgr.disconnect(uploadId);   // leaves room + closes socket, preventing connection leak
      managerRef.current = null;  // clear ref so next mount creates a fresh manager
      setState(initial);
    };
  }, [uploadId]);

  return state;
}
